import { FunctionComponent, memo, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ChannelContext, ConfigContext } from '../../../../../context';
import {
  OrganisationTag,
  OrganisationTagType,
  UserPreview,
  channelsNewApi,
} from '../../../../../services';
import { AutoComplete, TagsPicker, UserDisplayItem, useTags } from '../../../../../shared';

import classes from './StoryWriteAccess.module.scss';

interface StoryWriteAccessProps {
  userTags: OrganisationTag[];
  setUserTags: (userTags: OrganisationTag[]) => void;
  users: UserPreview[];
  setUsers: (users: UserPreview[]) => void;
}

export const StoryWriteAccess: FunctionComponent<StoryWriteAccessProps> = memo(
  ({ userTags, setUserTags, users, setUsers }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'createStorySettings' });

    const { hasUserTags } = useTags();

    const { id: channelId } = useContext(ChannelContext).channel;

    const { visible: emailVisible } =
      useContext(ConfigContext).config.elements.profile.fields.email;

    const [channelUsers] = channelsNewApi.endpoints.channelUsers.useLazyQuery();

    const fetchQuery = useCallback(
      (query: string, page?: number) => {
        return channelUsers({ channelId, page, filters: { query } });
      },
      [channelId, channelUsers]
    );

    const itemTemplate = useCallback(
      (user: UserPreview) => <UserDisplayItem {...user} showEmail={emailVisible} />,
      [emailVisible]
    );

    return (
      <div className={classes['story-write-access']}>
        {hasUserTags && (
          <TagsPicker
            tags={userTags}
            setTags={setUserTags}
            type={OrganisationTagType.USER}
            subTitle={ctxT('access-user-tags-sub-title')}
          />
        )}

        <div className={classes['story-write-access__individual-users']}>
          <div className={classes['story-write-access__title']}>
            {ctxT('access-individual-users-title')}
          </div>

          <div className={classes['story-write-access__subTitle']}>
            {ctxT('access-individual-users-sub-title')}
          </div>

          <AutoComplete
            value={users}
            fetchQuery={fetchQuery}
            itemTemplate={itemTemplate}
            propToIdentify={'id'}
            propToDisplay={'screenName'}
            placeholder={t('common.type-to-search')}
            onChange={setUsers}
          />
        </div>
      </div>
    );
  }
);
